import { useState, useEffect } from "react";
import { AlertModal } from "@/components/AlertModal";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CreditCard, Plus, Edit2, Trash2, Users, CheckCircle, XCircle, DollarSign } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";

export default function AdminMembership() {
  const [plans, setPlans] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ name: "", price: "", duration_days: 30, description: "", is_active: 1 });
  const [tab, setTab] = useState("plans");
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "" });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: null });

  const fetchData = () => {
    axios.get(`${API}/membership/plans/all`, { withCredentials: true }).then(res => setPlans(res.data));
    axios.get(`${API}/membership/subscribers`, { withCredentials: true }).then(res => setSubscribers(res.data));
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      setAlertModal({ isOpen: true, title: "Error", message: "Name and price are required" });
      return;
    }
    const method = editingPlan ? "put" : "post";
    const url = editingPlan ? `${API}/membership/plans/${editingPlan.id}` : `${API}/membership/plans`;
    await axios[method](url, {
      ...formData,
      price: parseFloat(formData.price),
      duration_days: parseInt(formData.duration_days),
    }, { withCredentials: true });
    setIsModalOpen(false);
    setEditingPlan(null);
    setFormData({ name: "", price: "", duration_days: 30, description: "", is_active: 1 });
    fetchData();
  };

  const handleDelete = (plan) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Plan",
      message: `Delete "${plan.name}"? This won't affect existing subscribers.`,
      onConfirm: async () => {
        await axios.delete(`${API}/membership/plans/${plan.id}`, { withCredentials: true });
        setConfirmModal({ isOpen: false });
        fetchData();
      }
    });
  };

  const activeSubscribers = subscribers.filter(s => s.is_active_member);

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-blue-500/10"><CreditCard className="text-blue-400" size={22} /></div>
            <div>
              <div className="text-2xl font-bold text-white">{plans.length}</div>
              <div className="text-xs text-slate-500">Plans</div>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-500/10"><Users className="text-emerald-400" size={22} /></div>
            <div>
              <div className="text-2xl font-bold text-white">{activeSubscribers.length}</div>
              <div className="text-xs text-slate-500">Active Members</div>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-purple-500/10"><DollarSign className="text-purple-400" size={22} /></div>
            <div>
              <div className="text-2xl font-bold text-white">{subscribers.length}</div>
              <div className="text-xs text-slate-500">Total Subscribers</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("plans")} className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${tab === "plans" ? "bg-blue-600 text-white" : "bg-slate-900 text-slate-400 hover:text-white border border-slate-800"}`} data-testid="plans-tab">Plans</button>
        <button onClick={() => setTab("subscribers")} className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${tab === "subscribers" ? "bg-blue-600 text-white" : "bg-slate-900 text-slate-400 hover:text-white border border-slate-800"}`} data-testid="subscribers-tab">Subscribers</button>
      </div>

      {tab === "plans" && (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => { setEditingPlan(null); setFormData({ name: "", price: "", duration_days: 30, description: "", is_active: 1 }); setIsModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-500 transition-colors"
              data-testid="add-plan-btn"
            >
              <Plus size={18} /> Add Plan
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan, idx) => (
              <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                className={`bg-slate-900 rounded-2xl border p-6 ${plan.is_active ? 'border-slate-800' : 'border-red-500/20 opacity-60'}`} data-testid={`plan-card-${plan.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                    <p className="text-slate-500 text-sm mt-1">{plan.description || "No description"}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${plan.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                    {plan.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">${plan.price}<span className="text-base text-slate-500 font-normal">/{plan.duration_days} days</span></div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-800">
                  <button onClick={() => { setEditingPlan(plan); setFormData({ name: plan.name, price: plan.price, duration_days: plan.duration_days, description: plan.description || "", is_active: plan.is_active }); setIsModalOpen(true); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-slate-400 rounded-lg text-xs font-medium hover:text-white transition-colors" data-testid={`edit-plan-${plan.id}`}>
                    <Edit2 size={14} /> Edit
                  </button>
                  <button onClick={() => handleDelete(plan)} className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500 hover:text-white transition-colors" data-testid={`delete-plan-${plan.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          {plans.length === 0 && <p className="text-center text-slate-500 py-12">No plans created yet. Click "Add Plan" to create your first membership plan.</p>}
        </>
      )}

      {tab === "subscribers" && (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full hidden lg:table" data-testid="subscribers-table">
              <thead><tr className="border-b border-slate-800">
                <th className="text-left p-4 text-xs font-bold text-slate-500 uppercase">User</th>
                <th className="text-left p-4 text-xs font-bold text-slate-500 uppercase">Plan</th>
                <th className="text-left p-4 text-xs font-bold text-slate-500 uppercase">Status</th>
                <th className="text-left p-4 text-xs font-bold text-slate-500 uppercase">Expires</th>
              </tr></thead>
              <tbody>
                {subscribers.map(sub => (
                  <tr key={sub.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="p-4">
                      <div className="font-medium text-white">{sub.name || sub.email}</div>
                      <div className="text-xs text-slate-500">{sub.email}</div>
                    </td>
                    <td className="p-4 text-sm text-slate-300">{sub.plan_name}</td>
                    <td className="p-4">
                      {sub.is_active_member ? (
                        <span className="flex items-center gap-1 text-emerald-400 text-sm"><CheckCircle size={14} /> Active</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-sm"><XCircle size={14} /> Expired</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-slate-400">{sub.membership_expires ? new Date(sub.membership_expires).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile cards */}
            <div className="lg:hidden space-y-3 p-4">
              {subscribers.map(sub => (
                <div key={sub.id} className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                  <div className="font-medium text-white">{sub.name || sub.email}</div>
                  <div className="text-xs text-slate-500">{sub.email}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="text-slate-400">{sub.plan_name}</span>
                    {sub.is_active_member ? (
                      <span className="text-emerald-400 flex items-center gap-1"><CheckCircle size={12} /> Active</span>
                    ) : (
                      <span className="text-red-400 flex items-center gap-1"><XCircle size={12} /> Expired</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {subscribers.length === 0 && <p className="text-center text-slate-500 py-12">No subscribers yet.</p>}
        </div>
      )}

      {/* Plan Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 rounded-2xl border border-slate-800 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-800">
              <h3 className="font-bold text-white text-lg">{editingPlan ? "Edit Plan" : "New Plan"}</h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-400">Plan Name</label>
                <input required type="text" data-testid="plan-name-input" placeholder="e.g. Premium Monthly"
                  className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400">Price (USD)</label>
                  <input required type="number" step="0.01" min="0.50" data-testid="plan-price-input" placeholder="9.99"
                    className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-400">Duration (Days)</label>
                  <input required type="number" min="1" data-testid="plan-duration-input" placeholder="30"
                    className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    value={formData.duration_days} onChange={e => setFormData({ ...formData, duration_days: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-400">Description</label>
                <textarea data-testid="plan-desc-input" placeholder="Plan features and benefits..."
                  className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 min-h-[80px]"
                  value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-950/50 rounded-xl border border-slate-800">
                <input type="checkbox" id="plan_active" className="w-5 h-5 rounded border-slate-800 bg-slate-950 text-blue-600"
                  checked={formData.is_active === 1} onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })} />
                <label htmlFor="plan_active" className="text-sm font-bold text-slate-400 cursor-pointer">Active (visible to users)</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 px-4 py-3 bg-slate-800 text-slate-400 rounded-xl font-bold hover:text-white transition-colors">Cancel</button>
                <button type="submit" data-testid="save-plan-btn" className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-colors">Save Plan</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <AlertModal isOpen={alertModal.isOpen} title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal({ ...alertModal, isOpen: false })} />
      <ConfirmModal isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })} />
    </>
  );
}
